import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  TrendingUp, IndianRupee, ShoppingCart, Target, 
  Loader2, RefreshCw, BarChart3, Megaphone, Eye, 
  MousePointerClick, ArrowUpRight, ArrowDownRight,
  Calendar, PieChart, Wallet, Sparkles, Lightbulb,
  ChevronRight, Zap, Users, Image, DollarSign
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, BarChart, Bar, Legend
} from 'recharts';
import { formatDateTimeIST } from '../../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons and colors for AI suggestions
const categoryConfig = {
  campaign: { icon: Megaphone, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  budget: { icon: DollarSign, color: 'bg-green-100 text-green-700 border-green-200' },
  creative: { icon: Image, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  targeting: { icon: Users, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  conversion: { icon: Zap, color: 'bg-pink-100 text-pink-700 border-pink-200' },
};

const priorityColors = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-700',
};

function AdminInsightsPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const token = localStorage.getItem('admin_token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/insights/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      setData(result);
    } catch (error) {
      toast.error('Failed to fetch business insights');
    } finally {
      setLoading(false);
    }
  };

  const fetchAISuggestions = async () => {
    if (!data) return;
    
    setLoadingAI(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/insights/ai-suggestions`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      
      if (result.error) {
        toast.error(result.error);
      } else {
        setAiSuggestions(result);
        toast.success('AI suggestions generated!');
      }
    } catch (error) {
      toast.error('Failed to generate AI suggestions');
    } finally {
      setLoadingAI(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const revenue = data?.revenue || {};
  const metaAds = data?.meta_ads || {};
  const metrics = data?.metrics || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <TrendingUp className="w-8 h-8" />
              Business Insights
            </h1>
            <p className="text-muted-foreground">Revenue tracking & Ad spend analysis</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* ROAS Card */}
          <Card className="p-5 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-green-100 text-sm">Return on Ad Spend (ROAS)</p>
                <p className="text-4xl font-bold mt-1">
                  {metrics.roas > 0 ? `${metrics.roas}x` : 'N/A'}
                </p>
                <p className="text-sm text-green-100 mt-2">
                  {metrics.roas >= 3 ? '🎉 Great! Profitable ads' : 
                   metrics.roas >= 2 ? '👍 Good performance' : 
                   metrics.roas > 0 ? '⚠️ Needs improvement' : 'Add campaigns to track'}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <Target className="w-6 h-6" />
              </div>
            </div>
          </Card>

          {/* Ad Spend % of Revenue */}
          <Card className="p-5 bg-gradient-to-br from-orange-500 to-red-500 text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-orange-100 text-sm">Ad Spend (% of Revenue)</p>
                <p className="text-4xl font-bold mt-1">
                  {metrics.ad_spend_percentage > 0 ? `${metrics.ad_spend_percentage}%` : '0%'}
                </p>
                <p className="text-sm text-orange-100 mt-2">
                  {metrics.ad_spend_percentage <= 20 ? '✅ Healthy ratio' : 
                   metrics.ad_spend_percentage <= 35 ? '⚠️ Monitor closely' : 
                   metrics.ad_spend_percentage > 35 ? '🚨 High ad costs' : 'No ads running'}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <PieChart className="w-6 h-6" />
              </div>
            </div>
          </Card>

          {/* Net After Ads */}
          <Card className="p-5 bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-100 text-sm">Gross After Ad Spend (30d)</p>
                <p className="text-4xl font-bold mt-1">
                  ₹{(metrics.gross_after_ads || 0).toLocaleString()}
                </p>
                <p className="text-sm text-blue-100 mt-2">
                  Revenue - Ad Spend
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <Wallet className="w-6 h-6" />
              </div>
            </div>
          </Card>
        </div>

        {/* Revenue Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Cards */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <IndianRupee className="w-5 h-5" />
              Revenue Overview
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Today */}
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Calendar className="w-4 h-4" />
                  Today
                </div>
                <p className="text-2xl font-bold">₹{(revenue.today?.revenue || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {revenue.today?.orders || 0} orders • AOV ₹{revenue.today?.aov || 0}
                </p>
              </Card>

              {/* Last 7 Days */}
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Calendar className="w-4 h-4" />
                  Last 7 Days
                </div>
                <p className="text-2xl font-bold">₹{(revenue.week?.revenue || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {revenue.week?.orders || 0} orders • AOV ₹{revenue.week?.aov || 0}
                </p>
              </Card>

              {/* Last 30 Days */}
              <Card className="p-4 border-green-200 bg-green-50/50">
                <div className="flex items-center gap-2 text-sm text-green-700 mb-1">
                  <Calendar className="w-4 h-4" />
                  Last 30 Days
                </div>
                <p className="text-2xl font-bold text-green-800">₹{(revenue.month?.revenue || 0).toLocaleString()}</p>
                <p className="text-xs text-green-600 mt-1">
                  {revenue.month?.orders || 0} orders • AOV ₹{revenue.month?.aov || 0}
                </p>
              </Card>

              {/* All Time */}
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <BarChart3 className="w-4 h-4" />
                  All Time
                </div>
                <p className="text-2xl font-bold">₹{(revenue.all_time?.revenue || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {revenue.all_time?.orders || 0} orders • AOV ₹{revenue.all_time?.aov || 0}
                </p>
              </Card>
            </div>

            {/* Payment Method Breakdown */}
            <Card className="p-4">
              <h3 className="font-medium mb-3">Payment Method (30 Days)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700">COD Revenue</p>
                  <p className="text-xl font-bold text-amber-800">
                    ₹{(revenue.month?.cod_revenue || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-amber-600">{revenue.month?.cod_orders || 0} orders</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">Prepaid Revenue</p>
                  <p className="text-xl font-bold text-green-800">
                    ₹{(revenue.month?.prepaid_revenue || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-green-600">{revenue.month?.prepaid_orders || 0} orders</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Revenue Chart */}
          <Card className="p-4">
            <h3 className="font-medium mb-4">Daily Revenue (Last 30 Days)</h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenue.daily_breakdown || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 10 }}
                    interval={2}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip 
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Meta Ad Spend Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            Meta Ad Spend (Tiffin & Bag Campaigns)
          </h2>

          {metaAds.error ? (
            <Card className="p-6 text-center">
              <p className="text-muted-foreground">{metaAds.error}</p>
              <p className="text-sm mt-2">Configure Meta in Settings → Marketing to track ad spend</p>
            </Card>
          ) : metaAds.campaigns?.length === 0 ? (
            <Card className="p-6 text-center">
              <Megaphone className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No campaigns created yet</p>
              <p className="text-sm mt-2">Create campaigns from Marketing → Meta Ads</p>
            </Card>
          ) : (
            <>
              {/* Ad Spend Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 border-red-200 bg-red-50/50">
                  <div className="flex items-center gap-2 text-sm text-red-700 mb-1">
                    <IndianRupee className="w-4 h-4" />
                    Total Spend
                  </div>
                  <p className="text-2xl font-bold text-red-800">
                    ₹{(metaAds.totals?.spend || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-red-600">Last 30 days</p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Eye className="w-4 h-4" />
                    Impressions
                  </div>
                  <p className="text-2xl font-bold">
                    {(metaAds.totals?.impressions || 0).toLocaleString()}
                  </p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <MousePointerClick className="w-4 h-4" />
                    Clicks
                  </div>
                  <p className="text-2xl font-bold">
                    {(metaAds.totals?.clicks || 0).toLocaleString()}
                  </p>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Target className="w-4 h-4" />
                    CTR
                  </div>
                  <p className="text-2xl font-bold">
                    {metaAds.totals?.ctr || 0}%
                  </p>
                </Card>
              </div>

              {/* Campaign Details */}
              <Card className="p-4">
                <h3 className="font-medium mb-4">Campaign Performance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-3">Campaign</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-right p-3">Budget/Day</th>
                        <th className="text-right p-3">Spend</th>
                        <th className="text-right p-3">Impressions</th>
                        <th className="text-right p-3">Clicks</th>
                        <th className="text-right p-3">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metaAds.campaigns?.map((campaign, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/30">
                          <td className="p-3">
                            <div className="font-medium">{campaign.name}</div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {campaign.id}
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant={campaign.campaign_type === 'retargeting' ? 'default' : 'secondary'}>
                              {campaign.campaign_type}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Badge className={
                              campaign.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                              campaign.status === 'PAUSED' ? 'bg-amber-100 text-amber-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {campaign.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">₹{campaign.daily_budget || 0}</td>
                          <td className="p-3 text-right text-red-600 font-medium">
                            ₹{campaign.insights?.spend?.toLocaleString() || 0}
                          </td>
                          <td className="p-3 text-right">
                            {campaign.insights?.impressions?.toLocaleString() || 0}
                          </td>
                          <td className="p-3 text-right">
                            {campaign.insights?.clicks?.toLocaleString() || 0}
                          </td>
                          <td className="p-3 text-right">
                            {campaign.insights?.ctr?.toFixed(2) || 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {metaAds.campaigns?.length === 0 && (
                  <p className="text-center py-4 text-muted-foreground">
                    No campaign data available
                  </p>
                )}
              </Card>
            </>
          )}
        </div>

        {/* Quick Tips */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <h3 className="font-medium text-blue-800 mb-2">💡 Business Tips</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>ROAS &gt; 3x</strong> is considered excellent for e-commerce</li>
            <li>• Keep <strong>Ad Spend under 25%</strong> of revenue for healthy margins</li>
            <li>• <strong>COD orders</strong> have higher fulfillment costs - factor in logistics</li>
            <li>• Track <strong>daily trends</strong> to spot patterns and optimize campaigns</li>
          </ul>
        </Card>

        {/* AI-Powered Suggestions Section */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">AI Marketing Advisor</h2>
                <p className="text-sm text-muted-foreground">Get personalized suggestions to improve your ROAS</p>
              </div>
            </div>
            <Button 
              onClick={fetchAISuggestions} 
              disabled={loadingAI || !data}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              {loadingAI ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {aiSuggestions ? 'Refresh Suggestions' : 'Get AI Suggestions'}
                </>
              )}
            </Button>
          </div>

          {!aiSuggestions && !loadingAI && (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <Lightbulb className="w-12 h-12 mx-auto text-amber-400 mb-3" />
              <p className="text-muted-foreground">Click "Get AI Suggestions" to analyze your business data</p>
              <p className="text-sm text-muted-foreground mt-1">AI will review your revenue, ad spend, and campaigns to provide actionable recommendations</p>
            </div>
          )}

          {loadingAI && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-purple-500 mb-3" />
              <p className="text-muted-foreground">Analyzing your business data...</p>
              <p className="text-sm text-muted-foreground mt-1">This may take 10-15 seconds</p>
            </div>
          )}

          {aiSuggestions && aiSuggestions.suggestions?.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                Generated: {formatDateTimeIST(aiSuggestions.generated_at)}
              </div>
              
              <div className="grid gap-4">
                {aiSuggestions.suggestions.map((suggestion, idx) => {
                  const config = categoryConfig[suggestion.category] || categoryConfig.campaign;
                  const CategoryIcon = config.icon;
                  
                  return (
                    <Card key={idx} className={`p-4 border ${config.color.split(' ')[2]}`}>
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${config.color.split(' ').slice(0, 2).join(' ')}`}>
                          <CategoryIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="font-semibold">{suggestion.title}</h4>
                            <Badge className={priorityColors[suggestion.priority]}>
                              {suggestion.priority} priority
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {suggestion.category}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {suggestion.suggestion}
                          </p>
                          {suggestion.expected_impact && (
                            <div className="flex items-center gap-1 text-sm text-green-600">
                              <TrendingUp className="w-4 h-4" />
                              <span>Expected: {suggestion.expected_impact}</span>
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Action Summary */}
              <Card className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Quick Action Summary
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-sm">
                  {Object.entries(
                    aiSuggestions.suggestions.reduce((acc, s) => {
                      acc[s.category] = (acc[s.category] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([category, count]) => {
                    const config = categoryConfig[category] || categoryConfig.campaign;
                    const CategoryIcon = config.icon;
                    return (
                      <div key={category} className={`p-2 rounded-lg ${config.color.split(' ').slice(0, 2).join(' ')}`}>
                        <CategoryIcon className="w-4 h-4 mx-auto mb-1" />
                        <div className="font-medium">{count}</div>
                        <div className="text-xs capitalize">{category}</div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          )}

          {aiSuggestions?.error && (
            <div className="text-center py-8 text-red-600">
              <p>{aiSuggestions.error}</p>
              <p className="text-sm mt-1">Please ensure OpenAI API key is configured in backend</p>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminInsightsPage;
